import React, { useEffect, useState } from 'react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { UserCheck, X, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { applicationsApi, getErrorMessage } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIVILEGED_ROLES = new Set(['admin', 'delivery_manager', 'team_lead']);

/**
 * Compact inline control for reassigning an application to a recruiter.
 * Used inside CandidateViewDialog's application rows so admins / TLs / DMs
 * can hand off a candidate's application to a specific recruiter without
 * leaving the dialog.
 *
 * Strict-RBAC pairing: applications recruiters can see are now driven by
 * `created_by == self OR assigned_to == self`. This control writes the
 * `assigned_to` side of that rule.
 */
const ApplicationAssignControl = ({
  applicationId,
  assignedTo,           // current assigned_to (user id or null)
  assigneeName,         // pre-resolved name (optional, just for display)
  onChange,             // (newAssignedToId, newAssigneeName) => void
}) => {
  const { user } = useAuth();
  const canEdit = !!user && PRIVILEGED_ROLES.has(user.role);

  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open || !canEdit || users.length > 0) return;
    setLoading(true);
    // Lightweight users list — we only need {id, name, email, role}.
    axios.get(`${API}/users`, { params: { page_size: 200 } })
      .then((res) => {
        const list = (res.data?.items || res.data || []).filter(
          (u) => u.role === 'recruiter' || u.role === 'team_lead' || u.role === 'delivery_manager',
        );
        setUsers(list);
      })
      .catch((err) => toast.error(getErrorMessage(err, 'Could not load recruiters')))
      .finally(() => setLoading(false));
  }, [open, canEdit, users.length]);

  if (!canEdit) {
    // Read-only chip for recruiters — they don't get to reassign, but
    // they can still see who currently owns the application.
    if (!assignedTo) return null;
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5"
        data-testid={`app-assigned-readonly-${applicationId}`}
        title={`Assigned to ${assigneeName || assignedTo}`}
      >
        <UserCheck className="w-3 h-3" />
        {assigneeName || 'Assigned'}
      </span>
    );
  }

  const persist = async (newId) => {
    setSaving(true);
    try {
      const res = await applicationsApi.assign(applicationId, newId);
      const data = res?.data || {};
      onChange?.(data.assigned_to || null, data.assignee_name || null);
      toast.success(newId ? `Assigned to ${data.assignee_name || 'recruiter'}` : 'Assignment cleared');
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update assignment'));
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (u.name || '').toLowerCase().includes(needle) || (u.email || '').toLowerCase().includes(needle);
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 border transition ${
            assignedTo
              ? 'border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
          data-testid={`app-assign-btn-${applicationId}`}
          title={assignedTo ? 'Reassign or clear' : 'Assign to a recruiter'}
        >
          <UserCheck className="w-3 h-3" />
          {assignedTo ? (assigneeName || 'Assigned') : 'Assign'}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-2"
        data-testid={`app-assign-popover-${applicationId}`}
      >
        <div className="flex items-center gap-1 px-2 py-1 border-b border-slate-100 mb-1">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search recruiters…"
            className="h-7 text-xs border-0 px-1 focus-visible:ring-0"
            disabled={saving}
            data-testid={`app-assign-search-${applicationId}`}
          />
        </div>
        {loading ? (
          <div className="px-2 py-3 text-xs text-slate-500 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-2 py-3 text-xs text-slate-500 italic">No matches</div>
        ) : (
          <ul className="max-h-60 overflow-y-auto">
            {filtered.map((u) => {
              const active = u.id === assignedTo;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => persist(u.id)}
                    disabled={saving || active}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition ${
                      active ? 'bg-teal-50 text-teal-800 cursor-default' : 'hover:bg-slate-50'
                    } disabled:opacity-60`}
                    data-testid={`app-assign-option-${applicationId}-${u.id}`}
                  >
                    <div className="font-medium truncate flex items-center gap-1.5">
                      {active ? <UserCheck className="w-3 h-3" /> : null}
                      {u.name || u.email}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {u.email} · {u.role}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {assignedTo ? (
          <div className="border-t border-slate-100 mt-1 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => persist(null)}
              disabled={saving}
              className="w-full h-7 text-[11px] text-rose-700 hover:bg-rose-50"
              data-testid={`app-assign-clear-${applicationId}`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <X className="w-3 h-3 mr-1" />}
              Clear assignment
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

export default ApplicationAssignControl;
